/* ============================================================
   ILS - PDP per-product reviews renderer (TASK-2034, TASK-2036).

   Powers two widgets on the product page:
     1. Top mini-carousel below SKU: `.ils-prw` with `[data-ils-prw-track]`
        (rebuilt slides + dots; up to 5 real reviews for this product,
        with or without photo — same behavior as before).
     2. Bottom "embed-revs" horizontal rail: `[data-ils-revs-pdp]`
        Photo-only, product-first, store-fallback (TASK-2036):
          a) reviews for THIS product WITH photo (newest first),
          b) if fewer than TARGET_MIN, top up with store-wide photo
             reviews from /api/reviews/featured (excluding this product),
          c) never show non-photo reviews unless we're literally out.

   The top rev-summary block ([data-ils-pdp-rev-summary]) is NOT
   patched here anymore — see custom-pdp-reviews-summary.js, which
   fills it with STORE-wide numbers (TASK-2036).

   Product id + handle come from a single script tag emitted by
   custom-main-product.liquid:
     window.ILS_PDP = { productId, productHandle, apiBase, featuredImage }

   On empty / fetch error the top carousel hides the demo card and
   the bottom rail is hidden entirely (never leave fake demo cards
   attributed to a real product).
   ============================================================ */
(function () {
  'use strict';

  var STAR_PATH = 'M12 2.6c.55 0 1.04.33 1.27.83l2.3 4.96 5.43.7c.55.07.99.45 1.16.97.16.51-.01 1.07-.42 1.43l-4.02 3.52 1.06 5.4c.1.54-.12 1.08-.57 1.4-.45.31-1.04.34-1.52.07L12 18.86l-4.69 2.61c-.48.27-1.07.24-1.52-.07-.45-.32-.67-.86-.57-1.4l1.06-5.4-4.02-3.52c-.41-.36-.58-.92-.42-1.43.17-.52.61-.9 1.16-.97l5.43-.7 2.3-4.96c.23-.5.72-.83 1.27-.83z';

  function starSvg(state) {
    if (state === 'full') return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + STAR_PATH + '"/></svg>';
    if (state === 'dim')  return '<svg viewBox="0 0 24 24" class="dim" aria-hidden="true"><path d="' + STAR_PATH + '"/></svg>';
    var gid = 'ilspdppg' + Math.random().toString(36).slice(2, 8);
    return '<svg viewBox="0 0 24 24" aria-hidden="true">' +
      '<defs><linearGradient id="' + gid + '"><stop offset="50%" stop-color="var(--ils-star)"/><stop offset="50%" stop-color="var(--ils-star-dim)"/></linearGradient></defs>' +
      '<path d="' + STAR_PATH + '" fill="url(#' + gid + ')"/></svg>';
  }

  function starsRowHtml(rate) {
    rate = parseFloat(rate) || 0;
    var html = '';
    for (var i = 1; i <= 5; i++) {
      if (rate >= i)            html += starSvg('full');
      else if (rate >= i - 0.5) html += starSvg('half');
      else                      html += starSvg('dim');
    }
    return html;
  }

  /* EU-27 flag SVGs - subset used on this store's audience. Matches
     custom-reviews-home.js so cards look identical between home and PDP. */
  var FLAGS = {
    DE:'<svg viewBox="0 0 5 3" preserveAspectRatio="none"><rect width="5" height="1" fill="#000"/><rect width="5" height="1" y="1" fill="#D00"/><rect width="5" height="1" y="2" fill="#FFCE00"/></svg>',
    LV:'<svg viewBox="0 0 5 3" preserveAspectRatio="none"><rect width="5" height="3" fill="#9E3039"/><rect width="5" height="1" y="1" fill="#fff"/></svg>',
    EE:'<svg viewBox="0 0 5 3" preserveAspectRatio="none"><rect width="5" height="1" fill="#0072CE"/><rect width="5" height="1" y="1" fill="#000"/><rect width="5" height="1" y="2" fill="#fff"/></svg>',
    LT:'<svg viewBox="0 0 5 3" preserveAspectRatio="none"><rect width="5" height="1" fill="#FDB913"/><rect width="5" height="1" y="1" fill="#006A44"/><rect width="5" height="1" y="2" fill="#C1272D"/></svg>'
  };

  var VERIFIED_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true">' +
      '<path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>' +
    '</svg>';

  /* Localized "verified" labels. English defaults; overridden in init() from
     the mount element's data-verified-label / data-verified-review-label
     attributes emitted by custom-main-product.liquid via `| t` (TASK-2566). */
  var VERIFIED_WORD = 'verified';
  var VERIFIED_ARIA = 'Verified review';

  function escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatDateDot(iso) {
    if (!iso) return '';
    var parts = String(iso).split('-');
    if (parts.length !== 3) return iso;
    var dd = parts[2].length === 1 ? '0' + parts[2] : parts[2].slice(0, 2);
    var mm = parts[1].length === 1 ? '0' + parts[1] : parts[1];
    return dd + '.' + mm + '.' + parts[0];
  }

  function reviewerLabel(r) {
    var name = (r.name || '').trim();
    var surname = (r.surname || '').trim();
    if (!name && !surname) return 'Customer';
    if (name && surname) return escapeHtml(name) + ' ' + escapeHtml(surname).charAt(0) + '.';
    return escapeHtml(name || surname);
  }

  /* ---------------- Top mini-carousel: .ils-prw ---------------- */

  function buildTopSlideHtml(r, isFirst, fallbackImage, totalSlides) {
    var reviewPhoto = (Array.isArray(r.photos) && r.photos.length) ? r.photos[0] : '';
    var photoSrc = reviewPhoto || fallbackImage || '';
    var country = (r.country || '').toUpperCase();
    var flagSvg = (country && FLAGS[country]) ? FLAGS[country] : '';
    var dateText = formatDateDot(r.date);
    var author = reviewerLabel(r);
    var body = escapeHtml(r.body || '');
    var showDots = totalSlides > 1;
    var dotsHtml = '';
    if (showDots) {
      dotsHtml += '<span class="ils-prw__dots" aria-hidden="true">';
      for (var i = 0; i < totalSlides; i++) {
        dotsHtml += '<span class="ils-prw__dot' + (i === 0 && isFirst ? ' is-on' : '') + '"></span>';
      }
      dotsHtml += '</span>';
    }
    var html = '';
    html += '<div class="ils-prw__slide' + (isFirst ? ' is-on' : '') + '">';
    html +=   '<div class="ils-prw__row">';
    html +=     '<div class="ils-prw__photo">';
    if (photoSrc) {
      html +=     '<img src="' + escapeHtml(photoSrc) + '" alt="" loading="lazy">';
    }
    html +=     '</div>';
    html +=     '<div class="ils-prw__content">';
    html +=       '<div class="ils-prw__rating">';
    html +=         '<span class="ils-prw__stars" aria-hidden="true">' + starsRowHtml(r.rating) + '</span>';
    if (dateText) {
      html +=       '<span class="ils-prw__date">' + escapeHtml(dateText) + '</span>';
    }
    html +=       '</div>';
    html +=       '<p class="ils-prw__text">' + body + '</p>';
    html +=       '<div class="ils-prw__author">';
    if (flagSvg) {
      html +=       '<span class="ils-prw__flag" aria-hidden="true">' + flagSvg + '</span>';
    }
    html +=         '<span class="ils-prw__name">' + author + '</span>';
    html +=         dotsHtml;
    if (r.verified === true) {
      html +=       '<span class="ils-prw__verified">' + VERIFIED_SVG + escapeHtml(VERIFIED_WORD) + '</span>';
    }
    html +=       '</div>';
    html +=     '</div>';
    html +=   '</div>';
    html += '</div>';
    return html;
  }

  function renderTopCarousel(reviews, fallbackImage) {
    var track = document.querySelector('[data-ils-prw-track]');
    if (!track) return;
    var card = track.closest('.ils-prw__card');

    if (!reviews.length) {
      // Zero real reviews for this product. Keep the card hidden so we never
      // show fabricated attribution (TASK-2548 — the Liquid no longer ships any
      // demo cards; the card is hidden by default and only revealed here). Header stays.
      if (card) card.hidden = true;
      return;
    }

    var slice = reviews.slice(0, 5);
    var html = '';
    for (var i = 0; i < slice.length; i++) {
      html += buildTopSlideHtml(slice[i], i === 0, fallbackImage, slice.length);
    }
    track.innerHTML = html;
    // Reveal the card now that it holds REAL reviews (hidden by default in Liquid).
    if (card) card.hidden = false;
    // Reset auto-rotation init flag on the track so custom-pdp-reviews-widget.js
    // will re-initialize on the fresh DOM (its MutationObserver handles this).
    track.dataset.ilsPrwInit = '';
  }

  /* --------- Bottom "embed-revs" carousel: [data-ils-revs-pdp] --------- */

  function buildEmbedCardHtml(r, fallbackImage) {
    var reviewPhoto = (Array.isArray(r.photos) && r.photos.length) ? r.photos[0] : '';
    var photoSrc = reviewPhoto || fallbackImage || '';
    var country = (r.country || '').toUpperCase();
    var flagSvg = (country && FLAGS[country]) ? FLAGS[country] : '';
    var dateText = formatDateDot(r.date);
    var author = reviewerLabel(r);
    var body = escapeHtml(r.body || '');
    var html = '';
    html += '<article class="ils-rev" data-id="' + escapeHtml(String(r.id || '')) + '">';
    html +=   '<div class="ils-rev__photo">';
    if (photoSrc) {
      html +=   '<img class="ils-rev__photo-img" src="' + escapeHtml(photoSrc) + '" alt="" loading="lazy">';
    }
    html +=   '</div>';
    html +=   '<div class="ils-rev__body">';
    html +=     '<div class="ils-rev__rating">';
    html +=       '<span class="ils-stars">' + starsRowHtml(r.rating) + '</span>';
    if (dateText) {
      html +=     '<time class="ils-rev__date">' + escapeHtml(dateText) + '</time>';
    }
    html +=     '</div>';
    html +=     '<p class="ils-rev__text">' + body + '</p>';
    html +=     '<div class="ils-rev__author">';
    if (flagSvg) {
      html +=     '<span class="ils-rev__flag">' + flagSvg + '</span>';
    }
    html +=       '<span class="ils-rev__meta"><span class="ils-rev__name">' + author + '</span></span>';
    if (r.verified === true) {
      html +=     '<span class="ils-rev__verified" aria-label="' + escapeHtml(VERIFIED_ARIA) + '">' + VERIFIED_SVG + '<span>' + escapeHtml(VERIFIED_WORD) + '</span></span>';
    }
    html +=     '</div>';
    html +=   '</div>';
    html += '</article>';
    return html;
  }

  function renderEmbedRail(reviews, fallbackImage) {
    var rail = document.querySelector('[data-ils-revs-pdp]');
    if (!rail) return;
    var wrap = rail.closest('.ils-pp__embed-revs') || rail;

    if (!reviews.length) {
      // Keep the entire embed-revs container hidden (it ships hidden in Liquid,
      // TASK-2548) so we never leave any card visible for a product with no
      // real photo reviews.
      wrap.hidden = true;
      return;
    }

    var html = '';
    for (var i = 0; i < reviews.length; i++) {
      html += buildEmbedCardHtml(reviews[i], fallbackImage);
    }
    rail.innerHTML = html;
    // Reveal now that the rail holds REAL reviews (hidden by default in Liquid).
    wrap.hidden = false;
  }

  /* ---------------- Init ---------------- */

  var EMBED_TARGET_MIN = 20;  // fill up to at least this many photo cards
  var EMBED_MAX        = 20;  // hard cap on rendered cards

  function hasPhoto(r) {
    return r && Array.isArray(r.photos) && r.photos.length > 0;
  }

  function init() {
    var top = document.querySelector('[data-ils-prw-track]');
    var embed = document.querySelector('[data-ils-revs-pdp]');
    if (!top && !embed) return;

    // Localized "verified" labels from the mount element (TASK-2566). English
    // fallback stays when the attribute is absent.
    var lblSrc = top || embed;
    if (lblSrc) {
      VERIFIED_WORD = lblSrc.getAttribute('data-verified-label') || VERIFIED_WORD;
      VERIFIED_ARIA = lblSrc.getAttribute('data-verified-review-label') || VERIFIED_ARIA;
    }

    var cfg = window.ILS_PDP || {};
    var apiBase = (typeof cfg.apiBase === 'string' && cfg.apiBase)
      ? cfg.apiBase.replace(/\/+$/, '') : '';
    var handle = (typeof cfg.productHandle === 'string') ? cfg.productHandle : '';
    var productId = (typeof cfg.productId === 'string' || typeof cfg.productId === 'number') ? String(cfg.productId) : '';
    var fallbackImage = (typeof cfg.featuredImage === 'string') ? cfg.featuredImage : '';

    if (!apiBase || (!handle && !productId)) {
      if (window.console && console.warn) {
        console.warn('[ils-pdp-by-product] missing config - keeping Liquid fallback.');
      }
      return;
    }

    var qs = [];
    if (handle) qs.push('handle=' + encodeURIComponent(handle));
    if (productId) qs.push('product_id=' + encodeURIComponent(productId));
    qs.push('limit=' + EMBED_MAX);
    var byProductUrl = apiBase + '/api/reviews/by-product?' + qs.join('&');

    fetch(byProductUrl, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var productReviews = (data && Array.isArray(data.reviews)) ? data.reviews : [];

        // Top mini-carousel: unchanged behavior — up to 5 latest product
        // reviews, photo optional (with fallbackImage if none).
        renderTopCarousel(productReviews, fallbackImage);

        // Embed rail: photo-first, product-first, store-fallback.
        var productPhotoReviews = productReviews.filter(hasPhoto);

        if (productPhotoReviews.length >= EMBED_TARGET_MIN) {
          renderEmbedRail(productPhotoReviews.slice(0, EMBED_MAX), fallbackImage);
          return;
        }

        // Need to top up with store-wide featured photo reviews.
        var need = EMBED_TARGET_MIN - productPhotoReviews.length;
        // Ask for extra so we can filter out the current product + dupes.
        var featuredLimit = need + productPhotoReviews.length + 8;
        var featuredUrl = apiBase + '/api/reviews/featured?limit=' + featuredLimit;

        fetch(featuredUrl, { credentials: 'omit' })
          .then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
          })
          .then(function (featData) {
            var featured = (featData && Array.isArray(featData.reviews)) ? featData.reviews : [];
            var seen = {};
            productPhotoReviews.forEach(function (r) { if (r && r.id != null) seen[String(r.id)] = true; });
            var storeFillers = featured.filter(function (r) {
              if (!hasPhoto(r)) return false;
              if (r.id != null && seen[String(r.id)]) return false;
              var h = r.product && r.product.handle;
              if (h && handle && h === handle) return false;
              return true;
            });
            var merged = productPhotoReviews.concat(storeFillers);
            // Target: at least EMBED_TARGET_MIN, cap at EMBED_MAX.
            var count = Math.max(EMBED_TARGET_MIN, productPhotoReviews.length);
            renderEmbedRail(merged.slice(0, Math.min(count, EMBED_MAX)), fallbackImage);
          })
          .catch(function (err) {
            // Fallback: at least render whatever product-photo reviews we have.
            renderEmbedRail(productPhotoReviews.slice(0, EMBED_MAX), fallbackImage);
            if (window.console && console.warn) {
              console.warn('[ils-pdp-by-product] featured fetch failed - showing product-only photos.', err);
            }
          });
      })
      .catch(function (err) {
        // Could not load real reviews. Hide both widgets rather than leave an
        // empty box — and NEVER fall back to fabricated demo content
        // (TASK-2548: the Liquid ships no hardcoded reviews anymore).
        var track = document.querySelector('[data-ils-prw-track]');
        var card = track && track.closest('.ils-prw__card');
        if (card) card.hidden = true;
        var rail = document.querySelector('[data-ils-revs-pdp]');
        var wrap = rail && (rail.closest('.ils-pp__embed-revs') || rail);
        if (wrap) wrap.hidden = true;
        if (window.console && console.warn) {
          console.warn('[ils-pdp-by-product] fetch failed - hiding review widgets (no fake fallback).', err);
        }
      });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
